import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

// Welcome shown to brand-new free users who received the starter music-video
// credit wallet. Existing (pre-feature) users default to 0 starter credits, so
// gating on `musicVideoCredits > 0` naturally targets new signups.
// Keyed by grant timestamp so a fresh grant (e.g. delete + re-register) greets
// again, while one grant never greets twice — even across browser sessions.
const SEEN_KEY = (userId: string, grantedAtMs: number) =>
  `solmi_mv_starter_welcome_seen_${userId}_${grantedAtMs}`;
// Greet ONLY brand-new signups: the card is a first-session welcome, not a
// recurring reminder. A user who already made a video (spending some but not
// all starter credits) kept seeing "your credits are ready" on every visit
// under the old 14-day window — founder feedback 2026-07-08. The window is
// anchored to the credit grant, NOT user.createdAt: popup-OAuth signups often
// don't render the authenticated app until minutes after the account row
// exists (the earlier 2-minute createdAt window silently missed them), and
// the localStorage seen-flag — not the window — is what prevents repeats.
const WELCOME_WINDOW_MS = 24 * 60 * 60 * 1000;
// How long the card stays before auto-dismissing.
const AUTO_DISMISS_MS = 60 * 1000;
const MUSIC_VIDEO_GENERATOR_PATH = "/ai-music-video-generator";

export default function StarterCreditsWelcome() {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [, navigate] = useLocation();

  const { data: subscription } = trpc.payment.getSubscription.useQuery(
    undefined,
    { enabled: isAuthenticated },
  );

  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const latchedRef = useRef(false);
  const grantedRef = useRef(0);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Decide once whether to greet this user.
  useEffect(() => {
    if (latchedRef.current) return;
    if (!isAuthenticated || !user?.id || !subscription) return;

    const credits = Number((subscription as any).musicVideoCredits) || 0;
    if (credits <= 0) return;

    const grantedAtRaw = (subscription as any).musicVideoCreditsGrantedAt;
    const grantedAtMs = grantedAtRaw ? new Date(grantedAtRaw).getTime() : 0;
    if (!grantedAtMs || Date.now() - grantedAtMs > WELCOME_WINDOW_MS) return;

    const key = SEEN_KEY(user.id, grantedAtMs);
    try {
      if (localStorage.getItem(key) === "1") return;
      localStorage.setItem(key, "1");
    } catch {
      // If storage is unavailable we still show it once per mount.
    }

    latchedRef.current = true;
    grantedRef.current = credits;
    setVisible(true);
  }, [isAuthenticated, user?.id, subscription]);

  const close = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setEntered(false);
    window.setTimeout(() => setVisible(false), 200);
  }, []);

  // Entrance animation + 1-minute auto-dismiss.
  useEffect(() => {
    if (!visible) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    dismissTimerRef.current = setTimeout(close, AUTO_DISMISS_MS);
    return () => {
      cancelAnimationFrame(raf);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [visible, close]);

  if (!visible) return null;

  const amount = grantedRef.current.toLocaleString();
  const goCreate = () => {
    navigate(MUSIC_VIDEO_GENERATOR_PATH);
    close();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed right-3 top-16 z-[70] w-[calc(100%-1.5rem)] max-w-[380px] sm:right-5 sm:top-20",
        "rounded-2xl border border-white/10 bg-[#202029]/95 p-5 text-white shadow-2xl backdrop-blur-xl",
        "transition-all duration-200 ease-out",
        entered ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
      )}
    >
      <button
        type="button"
        onClick={close}
        aria-label={t("starterCreditsWelcome.dismiss", {
          defaultValue: "Dismiss",
        })}
        className="absolute right-2.5 top-2.5 rounded-md p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>

      <div className="flex items-center gap-2.5 pr-6">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500/40 to-purple-500/40 ring-1 ring-inset ring-white/10">
          <Sparkles className="h-4 w-4 text-indigo-200" aria-hidden="true" />
        </span>
        <h3 className="text-base font-semibold leading-tight">
          {t("starterCreditsWelcome.title", {
            amount,
            defaultValue: "{{amount}} free music video credits",
          })}
        </h3>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-white/75">
        {t("starterCreditsWelcome.body", {
          amount,
          defaultValue:
            "Welcome to Solmi! Your {{amount}} free music video credits are ready — create your first music video.",
        })}
      </p>

      <button
        type="button"
        onClick={goCreate}
        className="mt-4 inline-flex items-center justify-center rounded-lg bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
      >
        {t("starterCreditsWelcome.cta", { defaultValue: "Make Music Video" })}
      </button>
    </div>
  );
}
